import React from 'react';
import { useIntl } from 'react-intl';
import { ImagePlacement, recommendedSizeFor } from '../../../../utils/imagePlacements';
import { ImageDisplaySizeFields } from '../../../pages-managment/pages-details/commun-component/ImageDisplaySizeFields';

export type ImageAttributeEdit = {
    url: string;
    seoUrl?: string;
    buttonLink?: string;
    displayWidth?: string;
    displayHeight?: string;
};

interface ImageEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedImage: { id?:string ,url: string; seoUrl?: string };
    onSave: (updatedImage: ImageAttributeEdit) => void;
    placement?: ImagePlacement | string;
    showAttributeFields?: boolean;
    buttonLink?: string;
    displayWidth?: string;
    displayHeight?: string;
}

const ImageEditModal: React.FC<ImageEditModalProps> = ({
    isOpen,
    onClose,
    selectedImage,
    onSave,
    placement,
    showAttributeFields = false,
    buttonLink = '',
    displayWidth = '',
    displayHeight = '',
}) => {
    const [seoUrl, setSeoUrl] = React.useState(selectedImage.seoUrl || '');
    const [newImage, setNewImage] = React.useState<File | null>(null);
    const [previewImage, setPreviewImage] = React.useState(selectedImage.url);
    const [link, setLink] = React.useState(buttonLink);
    const [width, setWidth] = React.useState(displayWidth);
    const [height, setHeight] = React.useState(displayHeight);
    const intl = useIntl()
    const sizeLabel = placement ? recommendedSizeFor(placement) : null;
    React.useEffect(() => {
        setSeoUrl(selectedImage.seoUrl || '');
        setPreviewImage(selectedImage.url);
        setNewImage(null);
        setLink(buttonLink);
        setWidth(displayWidth);
        setHeight(displayHeight);
    }, [selectedImage, buttonLink, displayWidth, displayHeight]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setNewImage(file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleSave = () => {
        const updatedImage = {
            url: newImage ? URL.createObjectURL(newImage) : selectedImage.url,
            seoUrl: seoUrl,
            buttonLink: link,
            displayWidth: width,
            displayHeight: height,
        };
        onSave(updatedImage);
    };

    if (!isOpen) return null;

    return (
        <div className="modal modal-style">
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{intl.formatMessage({
                            id: 'COMMON.UPDATE_IMAGE'
                        })}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <img
                            src={previewImage.includes('/images/') ? process.env.REACT_APP_API_URL_IMAGE + previewImage : previewImage}
                            alt="Selected"
                            className='modal-body-image'
                           
                        />
                        <div className="form-group mb-3">
                            <label>{intl.formatMessage({
                                id: 'COMMON.UPLOAD_IMAGE',
                            })}</label>
                            <input
                                type="file"
                                className="form-control"
                                accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                                onChange={handleImageChange}
                            />
                            {sizeLabel ? (
                              <div className='form-text text-muted mt-2 mb-0'>
                                {intl.formatMessage(
                                  { id: 'COMMON.IMAGE.RECOMMENDED_SIZE' },
                                  { size: sizeLabel },
                                )}
                              </div>
                            ) : null}
                        </div>
                        {typeof selectedImage.url === 'string' && (
                            <div className="form-group">
                                <label>{intl.formatMessage({
                                    id: 'COMMON.ALT_IMAGE',
                                })} </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={seoUrl}
                                    onChange={(e) => setSeoUrl(e.target.value)}
                                />
                            </div>
                        )}
                        {showAttributeFields && (
                            <>
                                <div className="form-group mt-3">
                                    <label>{intl.formatMessage({ id: 'BLOCK.BUTTON_LINK' })}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={link}
                                        onChange={(e) => setLink(e.target.value)}
                                        placeholder="https://…"
                                    />
                                </div>
                                <ImageDisplaySizeFields
                                    width={width}
                                    height={height}
                                    onChange={({width: nextWidth, height: nextHeight}) => {
                                        setWidth(nextWidth)
                                        setHeight(nextHeight)
                                    }}
                                />
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>{intl.formatMessage({
                            id: 'HEADER.RESET',
                        })}</button>
                        <button type="button" className="btn btn-primary" onClick={handleSave}>{intl.formatMessage({
                            id: 'HEADER.SAVE',
                        })}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageEditModal;